import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SpinLogo from '../components/Logo';
import Wheel from '../components/Wheel';
import { fr } from '../i18n/fr';

export default function Landing() {
  const { currentUser, isOwner } = useAuth();
  const [isSpinning, setIsSpinning] = useState(false);

  // This page is the B2B sales pitch for establishment owners — a logged-in
  // customer (non-owner) has no use for it, send them to their rewards instead.
  if (currentUser && !isOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  const ownerSteps = [
    { num: '01', title: fr.landing_step1_title, desc: fr.landing_step1_desc },
    { num: '02', title: fr.landing_step2_title, desc: fr.landing_step2_desc },
    { num: '03', title: fr.landing_step3_title, desc: fr.landing_step3_desc },
    { num: '04', title: fr.landing_step4_title, desc: fr.landing_step4_desc },
  ];

  const ownerFeatures = [
    { title: fr.landing_feat1_title, desc: fr.landing_feat1_desc },
    { title: fr.landing_feat2_title, desc: fr.landing_feat2_desc },
    { title: fr.landing_feat3_title, desc: fr.landing_feat3_desc },
  ];

  const customerSteps = [
    { num: '01', title: fr.landing_cstep1_title, desc: fr.landing_cstep1_desc },
    { num: '02', title: fr.landing_cstep2_title, desc: fr.landing_cstep2_desc },
    { num: '03', title: fr.landing_cstep3_title, desc: fr.landing_cstep3_desc },
  ];

  return (
    <div style={{ background: 'var(--paper)' }}>

      {/* HERO — light gradient stage */}
      <section className="stage" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-20 md:pt-20 md:pb-24">
          <div className="md:grid md:grid-cols-2 md:gap-16 md:items-center">

            <div className="flex flex-col items-center text-center md:items-start md:text-left">
              <div style={{
                fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: 'var(--ember-dark)', fontWeight: 700, fontFamily: 'Sora, system-ui',
                marginBottom: 20,
              }}>
                {fr.landing_eyebrow}
              </div>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  fontFamily: 'Sora, system-ui', fontWeight: 800,
                  fontSize: 'clamp(36px, 5.5vw, 64px)', lineHeight: 1.04,
                  letterSpacing: '-0.02em', color: 'var(--ink)', margin: '0 0 20px',
                }}
              >
                {fr.landing_h1_line1}<br />
                <span style={{ color: 'var(--ember-dark)' }}>{fr.landing_h1_line2}</span><br />
                {fr.landing_h1_line3}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                style={{ fontSize: 16, color: 'var(--muted-on-ink)', maxWidth: 420, lineHeight: 1.6, marginBottom: 32 }}
              >
                {fr.landing_subheadline}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="w-full flex flex-col sm:flex-row gap-3 mb-12"
              >
                {currentUser ? (
                  <Link to={isOwner ? '/admin' : '/dashboard'}
                    className="btn-gold text-center py-4 px-7 text-base font-bold">
                    {isOwner ? fr.landing_go_dashboard : fr.landing_go_rewards}
                  </Link>
                ) : (
                  <>
                    <Link to="/login?intent=owner" className="btn-gold text-center py-4 px-7 text-base font-bold">
                      {fr.landing_cta_primary}
                    </Link>
                    <a href="#how-it-works" className="btn-secondary-on-ink text-center py-4 px-7 text-base">
                      {fr.landing_cta_secondary}
                    </a>
                  </>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="w-full grid grid-cols-3 gap-6"
                style={{ borderTop: '1px solid var(--line-on-ink)', paddingTop: 20, maxWidth: 420 }}
              >
                {[['10K+', fr.landing_stat_reviews], ['5 min', fr.landing_stat_setup], ['€ROI', fr.landing_stat_revenue]].map(([val, lbl]) => (
                  <div key={lbl}>
                    <div className="tabular" style={{ fontFamily: 'Sora, system-ui', fontWeight: 700, fontSize: 20, color: 'var(--ember-dark)' }}>{val}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted-on-ink)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 3 }}>{lbl}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mt-14 md:mt-0"
            >
              <Wheel
                isSpinning={isSpinning}
                handleSpin={() => setIsSpinning(true)}
                onSpinComplete={() => setIsSpinning(false)}
                disabled={false}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — OWNER STEPS */}
      <section id="how-it-works" style={{ padding: '72px 0 56px' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div style={{
            fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'var(--ember)', fontWeight: 700, fontFamily: 'Sora, system-ui',
            marginBottom: 10,
          }}>
            Prêt en 5 minutes
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              fontFamily: 'Sora, system-ui', fontWeight: 800, fontSize: 'clamp(26px, 3.4vw, 38px)',
              color: 'var(--ink)', marginBottom: 40, letterSpacing: '-0.01em', maxWidth: 560,
            }}
          >
            {fr.landing_steps_heading}
          </motion.h2>

          <div className="grid gap-px md:grid-cols-4" style={{ background: 'var(--line)', border: '1px solid var(--line)' }}>
            {ownerSteps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                style={{ background: '#fff', padding: 24 }}
              >
                <div className="tabular" style={{ fontFamily: 'Sora, system-ui', fontWeight: 700, fontSize: 12, color: 'var(--ember)', letterSpacing: '0.08em', marginBottom: 14 }}>{step.num}</div>
                <div style={{ fontFamily: 'Sora, system-ui', fontWeight: 700, fontSize: 15, color: 'var(--ink)', marginBottom: 6 }}>{step.title}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{step.desc}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid gap-6 md:grid-cols-3 mt-14">
            {ownerFeatures.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                style={{ borderLeft: '2px solid var(--gold)', paddingLeft: 16 }}
              >
                <div style={{ fontFamily: 'Sora, system-ui', fontWeight: 700, fontSize: 15, color: 'var(--ink)', marginBottom: 4 }}>{f.title}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{f.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER EXPERIENCE */}
      <section style={{ padding: '56px 0', background: 'var(--paper-dim)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div style={{
            fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'var(--ember)', fontWeight: 700, fontFamily: 'Sora, system-ui',
            marginBottom: 10,
          }}>
            Expérience client
          </div>
          <div className="md:flex md:items-end md:justify-between md:gap-8" style={{ marginBottom: 36 }}>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{
                fontFamily: 'Sora, system-ui', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 32px)',
                color: 'var(--ink)', letterSpacing: '-0.01em', margin: 0, maxWidth: 480,
              }}
            >
              {fr.landing_customer_heading}
            </motion.h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 340, marginTop: 10 }}>
              {fr.landing_customer_sub}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {customerSteps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--radius-lg)', padding: 20 }}
              >
                <div className="tabular" style={{ fontFamily: 'Sora, system-ui', fontWeight: 700, fontSize: 22, color: 'var(--ember)', marginBottom: 10 }}>
                  {step.num}
                </div>
                <div style={{ fontFamily: 'Sora, system-ui', fontWeight: 700, fontSize: 15, color: 'var(--ink)', marginBottom: 4 }}>{step.title}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{step.desc}</div>
              </motion.div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 20, letterSpacing: '0.02em' }}>
            {fr.landing_customer_caption}
          </p>
        </div>
      </section>

      {/* CTA SECTION — light gradient stage */}
      {!currentUser && (
        <section className="stage" style={{ padding: '72px 24px' }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center flex flex-col items-center"
          >
            <div style={{ marginBottom: 20 }}>
              <SpinLogo size={72} />
            </div>
            <h2 style={{
              fontFamily: 'Sora, system-ui', fontWeight: 800, fontSize: 'clamp(26px, 3.6vw, 40px)',
              color: 'var(--ink)', marginBottom: 12, letterSpacing: '-0.01em',
            }}>
              Prêt à <span style={{ color: 'var(--ember-dark)' }}>{fr.landing_bottom_cta_q}</span>
            </h2>
            <p style={{ fontSize: 15, color: 'var(--muted-on-ink)', marginBottom: 28, lineHeight: 1.55, maxWidth: 420 }}>
              {fr.landing_bottom_cta_body}
            </p>
            <Link to="/login?intent=owner" className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-bold">
              {fr.landing_cta_primary}
            </Link>
            <div style={{ marginTop: 16 }}>
              <Link to="/login?intent=owner" style={{ fontSize: 13, color: 'var(--muted-on-ink)', textDecoration: 'underline' }}>
                {fr.landing_bottom_cta_signin}
              </Link>
            </div>
          </motion.div>
        </section>
      )}
    </div>
  );
}
